import { Navigate } from 'react-router-dom';
import { PageLoader } from '../shared/ui/PageLoader.js';
import { authGate, GATE_PATH, useAuth } from '../features/auth/index.js';
import { homePath } from './activeBusiness.js';
import { BusinessesProvider, useMyBusinesses } from './BusinessesContext.js';

/**
 * What `/` is.
 *
 * Signed out, it is the sign-in page: the app's front door is the door, and the
 * marketing page sits beside it at `/landing` for anyone who wants to be sold
 * to first. Signed in, `/` is a signpost — nobody stays here, they are sent to
 * the one screen that answers "what am I looking at" for their account shape.
 *
 * A redirect rather than `<SignInPage/>` rendered in place, so sign-in keeps
 * ONE canonical URL: two paths showing the same form is two things to keep in
 * step, and `SignInPage` already bounces a signed-in visitor back here.
 *
 * The auth check has to finish before either branch runs. Firebase resolves the
 * persisted session asynchronously, so `user` is null for the first frames of
 * every reload — redirecting on that null would throw an owner out to sign-in
 * on every refresh of their own dashboard.
 */
export function HomeRoute() {
  const gate = authGate(useAuth());

  if (gate === 'loading') {
    return <PageLoader />;
  }
  if (gate !== 'ready') return <Navigate to={GATE_PATH[gate]} replace />;

  // Mounted only for a signed-in visitor with a CONFIRMED email: the provider
  // opens live Firestore listeners on `businesses` and `accounts/{uid}`, which
  // the rules refuse to anyone else — signed out, or not yet verified (doc 88).
  return (
    <BusinessesProvider>
      <HomeDestination />
    </BusinessesProvider>
  );
}

/** Waits for the owner's businesses, then hands off to {@link homePath} — the
 *  rule itself is pure and unit-tested in `activeBusiness.test.ts`. */
function HomeDestination() {
  const { active, loading } = useMyBusinesses();

  if (loading) {
    return <PageLoader />;
  }
  return <Navigate to={homePath(active)} replace />;
}
